import { useEffect, useState } from "react";
import PortalNav from "../components/PortalNav";
import { api, formatDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Trash2, ArrowLeft, Plus } from "lucide-react";

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null); // null=list, {id?, title, body}
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/journal"); setEntries(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.body?.trim()) return toast.error("Write something first.");
    setBusy(true);
    try {
      if (editing.id) {
        await api.patch(`/journal/${editing.id}`, { title: editing.title || "", body: editing.body });
      } else {
        await api.post("/journal", { title: editing.title || "", body: editing.body });
      }
      toast.success("Saved.");
      setEditing(null); load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/journal/${id}`); toast.success("Deleted."); setConfirmDel(null); setEditing(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="portal-body" data-testid="journal-page">
      <Toaster position="top-center" />
      <header style={{ background: "var(--charcoal)", color: "var(--ivory)", padding: "28px 24px 32px" }}>
        <div className="container-x" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <p className="eyebrow eyebrow-light">Journal</p>
            <h1 style={{ fontFamily: "var(--serif)", fontWeight: 300, fontSize: "clamp(1.8rem, 4vw, 2.4rem)", margin: "10px 0" }}>
              {editing ? (editing.id ? "Edit entry" : "New entry") : "Your story."}
            </h1>
          </div>
          {!editing && (
            <button onClick={() => setEditing({ title: "", body: "" })} className="btn btn-primary" style={{ padding: "12px 20px" }} data-testid="journal-new">
              <Plus size={14} style={{ marginRight: 6 }} /> New
            </button>
          )}
        </div>
      </header>

      <main className="container-x" style={{ padding: "32px 24px" }}>
        {editing ? (
          <div className="portal-card" data-testid="journal-editor">
            <button onClick={() => setEditing(null)} className="btn-ghost" style={{ marginBottom: 20 }} data-testid="journal-back">
              <ArrowLeft size={14} style={{ marginRight: 6 }} /> Back
            </button>
            <p style={{ fontSize: ".72rem", letterSpacing: ".2em", textTransform: "uppercase", color: "var(--warm-gray)", marginBottom: 12 }}>
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </p>
            <input
              type="text" placeholder="Title (optional)"
              value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              style={{ width: "100%", fontFamily: "var(--serif)", fontSize: "1.8rem", fontWeight: 300, border: "none", borderBottom: "1px solid rgba(184,150,90,.3)", outline: "none", background: "transparent", padding: "8px 0", marginBottom: 20, color: "var(--charcoal)" }}
              data-testid="journal-title"
            />
            <textarea
              placeholder="What's rising in you today?"
              value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              style={{ width: "100%", minHeight: 300, fontFamily: "var(--sans)", fontSize: "1rem", lineHeight: 1.8, border: "none", outline: "none", background: "transparent", color: "var(--charcoal)", resize: "vertical" }}
              data-testid="journal-body"
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, gap: 12 }}>
              <button onClick={save} disabled={busy} className="btn btn-primary" data-testid="journal-save">
                {busy ? "Saving…" : "Save Entry"}
              </button>
              {editing.id && (
                <button onClick={() => setConfirmDel(editing.id)} className="btn btn-outline-dark" data-testid="journal-delete" style={{ color: "var(--charcoal)" }}>
                  <Trash2 size={14} style={{ marginRight: 6 }} /> Delete
                </button>
              )}
            </div>
          </div>
        ) : entries.length === 0 ? (
          <div className="portal-card" style={{ textAlign: "center", padding: 60 }} data-testid="journal-empty">
            <h3>Your story is waiting to be written.</h3>
            <p className="body-sm" style={{ marginTop: 12, marginBottom: 24 }}>Every entry is only visible to you.</p>
            <button onClick={() => setEditing({ title: "", body: "" })} className="btn btn-primary">Write your first entry</button>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }} data-testid="journal-list">
            {entries.map((e) => (
              <button key={e.id} onClick={() => setEditing(e)}
                className="portal-card" style={{ textAlign: "left", cursor: "pointer", border: "1px solid rgba(184,150,90,.2)" }}
                data-testid={`journal-item-${e.id}`}>
                <p style={{ fontSize: ".72rem", letterSpacing: ".2em", textTransform: "uppercase", color: "var(--warm-gray)", marginBottom: 8 }}>
                  {new Date(e.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                </p>
                {e.title && <h3 style={{ margin: "0 0 6px" }}>{e.title}</h3>}
                <p className="body-sm" style={{ margin: 0 }}>{(e.body || "").slice(0, 140)}{e.body?.length > 140 ? "…" : ""}</p>
              </button>
            ))}
          </div>
        )}
      </main>

      {confirmDel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} data-testid="delete-modal">
          <div className="portal-card" style={{ maxWidth: 400, margin: "0 24px" }}>
            <h3>Delete this entry?</h3>
            <p className="body-sm">This cannot be undone.</p>
            <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
              <button onClick={() => setConfirmDel(null)} className="btn btn-outline-dark" style={{ flex: 1 }} data-testid="delete-cancel">Keep it</button>
              <button onClick={() => del(confirmDel)} className="btn btn-primary" style={{ flex: 1, background: "#8B2A2A", borderColor: "#8B2A2A" }} data-testid="delete-confirm">Delete</button>
            </div>
          </div>
        </div>
      )}
      <PortalNav />
    </div>
  );
}
